import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, FileWarning, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api, { FiasTransferPreview } from '@/lib/api';

/**
 * The Fias viewer — server-rendered page images for `viewOnly` and
 * `pageAnalytics` links.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS DOES AND DOES NOT PROTECT — COPY RULES ARE SECURITY CONTROLS HERE
 *
 * The original file never reaches the browser: the server rasterizes each page
 * to a PNG, so there is no PDF in the network tab and nothing for the browser
 * to "save as". That is a real property and it is worth having.
 *
 * It is NOT copy protection. Anything rendered can be screenshotted, and any
 * text can be retyped. **Never label this "protected", "secure", or
 * "download disabled" as though it prevented copying.** The honest promise is
 * that casual redistribution is deterred and — with a watermark — attributable.
 * The banner below says exactly that and must not be softened into a claim.
 *
 * There is deliberately no right-click blocker and no `user-select: none`
 * theatre. They stop nobody, and they signal a guarantee the product cannot
 * make.
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * DWELL REPORTING IS BEST-EFFORT AND MUST NEVER BLOCK READING
 *
 * Page timings are batched and flushed on unmount and on tab-hide, using
 * `sendBeacon` where available — a reader closing the tab is the COMMON case,
 * not an edge case, and a normal fetch is cancelled on unload.
 *
 * Every failure is swallowed. A telemetry endpoint returning 500 must not stop
 * an investor reading a term sheet; the sender loses a data point, which is the
 * correct trade. The server clamps and caps whatever arrives, so nothing here
 * is trusted anyway.
 * ---------------------------------------------------------------------------
 */
export function TransferDocumentViewer({
  token,
  documentId,
  onClose,
}: {
  token: string;
  documentId: string;
  onClose: () => void;
}) {
  /**
   * The page WINDOWS loaded so far, in order.
   *
   * The server renders at most `pageSpan` pages per request and spends one unit
   * of the session's per-window render budget doing it, so a long document
   * arrives in pieces. Keeping the windows rather than one flat page array
   * means `meta` always describes the LAST response — which is where
   * `rendersRemaining` and the next `from` come from.
   */
  const [windows, setWindows] = useState<FiasTransferPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moreError, setMoreError] = useState<string | null>(null);

  const preview = windows[0] ?? null;
  const latest = windows[windows.length - 1] ?? null;
  const pages = useMemo(() => windows.flatMap((w) => w.pages), [windows]);

  /** pageNumber → accumulated ms. A ref, not state: updating it must never
   * re-render the viewer mid-scroll. */
  const dwellRef = useRef<Map<number, number>>(new Map());
  const visibleSinceRef = useRef<Map<number, number>>(new Map());
  const reportsPagesRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    setWindows([]);
    setMoreError(null);

    api
      .getFiasTransferPreview(token, documentId)
      .then((result) => {
        if (cancelled) return;
        setWindows([result]);
        reportsPagesRef.current = result.reportsPages;
      })
      .catch(() => {
        if (cancelled) return;
        // The server refuses rather than degrading — too large, not a PDF, or
        // a render failure. It never falls back to the original file, so there
        // is nothing to offer instead.
        setError('This file cannot be previewed.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, documentId]);

  // Flush on unmount AND on tab-hide. Unmount alone misses the most common
  // exit — closing the tab — because unload cancels in-flight fetches.
  useEffect(() => {
    const flush = () => {
      // Close any still-open intervals so the final page is not lost.
      const now = Date.now();
      for (const [pageNumber, since] of visibleSinceRef.current.entries()) {
        dwellRef.current.set(pageNumber, (dwellRef.current.get(pageNumber) ?? 0) + (now - since));
      }
      visibleSinceRef.current.clear();
      flushPageReports(token, documentId, dwellRef.current, reportsPagesRef.current);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flush();
    };
  }, [token, documentId]);

  const pageRefs = useRef<Map<number, HTMLElement>>(new Map());

  // IntersectionObserver rather than scroll math: it reports what is actually
  // on screen without a listener firing on every frame, and it is correct for
  // zoom, rotation, and a page taller than the viewport.
  useEffect(() => {
    if (pages.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const now = Date.now();
        for (const entry of entries) {
          const pageNumber = Number((entry.target as HTMLElement).dataset.pageNumber);
          if (!Number.isFinite(pageNumber)) continue;

          if (entry.isIntersecting) {
            if (!visibleSinceRef.current.has(pageNumber)) {
              visibleSinceRef.current.set(pageNumber, now);
            }
          } else {
            const since = visibleSinceRef.current.get(pageNumber);
            if (since !== undefined) {
              visibleSinceRef.current.delete(pageNumber);
              dwellRef.current.set(
                pageNumber,
                (dwellRef.current.get(pageNumber) ?? 0) + (now - since),
              );
            }
          }
        }
      },
      // Half the page on screen counts as reading it. A lower threshold makes
      // every page "read" during a fast scroll to the end.
      { threshold: 0.5 },
    );

    for (const element of pageRefs.current.values()) observer.observe(element);
    return () => observer.disconnect();
    // Re-observes when a new window is appended — a page that is not being
    // watched reports no dwell at all, which reads to the sender as "nobody
    // opened it" rather than "the observer was never attached".
  }, [pages]);

  /**
   * Whether more pages exist AND are reachable.
   *
   * Two separate ceilings, and conflating them would mislead the reader:
   * `truncated` means the DOCUMENT is longer than the renderer will ever serve
   * (nothing loads that), while running out of window budget means these pages
   * exist and are simply not available until a new download window opens.
   */
  const nextFrom = latest ? latest.to + 1 : 1;
  const renderableEnd = latest
    ? latest.truncated
      ? Math.min(latest.totalPages, latest.from + latest.pages.length - 1)
      : latest.totalPages
    : 0;
  const hasMore = Boolean(latest) && nextFrom <= renderableEnd;
  const budgetSpent = Boolean(latest) && latest.rendersRemaining <= 0;

  const loadMore = useCallback(async () => {
    if (!latest || loadingMore) return;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const next = await api.getFiasTransferPreview(token, documentId, {
        from: nextFrom,
        to: nextFrom + latest.pageSpan - 1,
      });
      setWindows((current) => [...current, next]);
    } catch {
      // Almost always the per-window render budget. Say what it is: the reader
      // has done nothing wrong and there is a concrete way forward.
      setMoreError(
        'No more pages can be rendered right now. Open a new download window to continue reading.',
      );
    } finally {
      setLoadingMore(false);
    }
  }, [token, documentId, latest, nextFrom, loadingMore]);

  const truncationNote = useMemo(() => {
    if (!latest?.truncated) return null;
    return `This document is longer than the viewer can render. Showing the first ${renderableEnd} of ${latest.totalPages} pages.`;
  }, [latest, renderableEnd]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate font-medium">{preview?.name ?? 'Preview'}</h2>
          <p className="text-xs text-muted-foreground">
            {/* The honest claim — see the copy rules in the module header. */}
            View only — this deters casual sharing and, when watermarked, makes it attributable. It
            does not prevent copying.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close preview">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto bg-muted/30 p-4">
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="py-12 text-center">
            <FileWarning className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {preview && (
          <div className="mx-auto max-w-3xl space-y-4">
            {truncationNote && (
              <p className="text-center text-sm text-muted-foreground">{truncationNote}</p>
            )}
            {pages.map((page) => (
              <figure
                key={page.pageNumber}
                data-page-number={page.pageNumber}
                ref={(element) => {
                  if (element) pageRefs.current.set(page.pageNumber, element);
                  else pageRefs.current.delete(page.pageNumber);
                }}
                className="overflow-hidden rounded-lg border bg-white shadow-sm"
              >
                <img
                  src={page.dataUri}
                  alt={`Page ${page.pageNumber}`}
                  width={page.width}
                  height={page.height}
                  className="h-auto w-full"
                  loading="lazy"
                />
              </figure>
            ))}

            {/* Paged loading, made visible rather than silent: a reader who
                cannot tell whether a document ended or merely stopped loading
                will assume the former. */}
            {hasMore && (
              <div className="space-y-2 py-4 text-center">
                <Button
                  variant="outline"
                  onClick={() => void loadMore()}
                  disabled={loadingMore || budgetSpent}
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading pages…
                    </>
                  ) : (
                    `Load pages ${nextFrom}–${Math.min(nextFrom + (latest?.pageSpan ?? 10) - 1, renderableEnd)} of ${renderableEnd}`
                  )}
                </Button>
                {(moreError || budgetSpent) && (
                  <p className="text-sm text-muted-foreground">
                    {moreError ??
                      'No more pages can be rendered in this session. Open a new download window to continue reading.'}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Send accumulated timings.
 *
 * `sendBeacon` first: it survives the page going away, which a fetch does not.
 * Failures are swallowed at every level — losing a data point is always
 * preferable to interrupting the reader.
 */
function flushPageReports(
  token: string,
  documentId: string,
  dwell: Map<number, number>,
  reportsPages: boolean,
): void {
  if (!reportsPages || dwell.size === 0) return;

  const pages = Array.from(dwell.entries())
    // Under a second is a scroll-past, not a read. Filtering here keeps the
    // sender's numbers meaningful and keeps the batch under its cap.
    .filter(([, ms]) => ms >= 1000)
    .map(([pageNumber, ms]) => ({ pageNumber, dwellMs: Math.round(ms) }));
  dwell.clear();
  if (pages.length === 0) return;

  const url = `/v1/public/fias-transfer/${encodeURIComponent(token)}/files/${encodeURIComponent(documentId)}/pages`;
  const payload = JSON.stringify({ pages });

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void api.reportFiasTransferPages(token, documentId, pages).catch(() => undefined);
  } catch {
    // Telemetry must never surface to the reader.
  }
}
